import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  apiGetBillingUsersUserUuidAccount,
  apiPutBillingUsersUserUuidCredits,
} from "../../../api";
import { RequestError } from "../../../api/api-connector";
import { SetCreditsRequestSchema } from "../../../api/generated/schemas.gen";
import { errorText } from "../../../app/error-text";
import { maybeReportError } from "../../../app/errors";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { RecordDialogTitle } from "../../../components/ui-kit/record-dialog-chrome";
import { Spinner } from "../../../components/ui-kit/spinner";
import { useFetch } from "../../../utils/use-fetch";

const BALANCE = SetCreditsRequestSchema.properties.balance;
const NOTE = SetCreditsRequestSchema.properties.note;

/**
 * A screening user's credit balance, loaded when the edit dialog opens and
 * written back on save. `PUT /credits` sets an exact figure and posts the
 * difference to the ledger, which needs a note - so changing the balance
 * asks for the reason.
 */
export function useScreeningCredits(
  /** Null for anyone but a screening user: nothing is loaded or saved. */
  userUuid: string | null,
) {
  const [state] = useState(() =>
    observable<CreditsState>({
      original: undefined,
      balance: undefined,
      note: "",
      error: null,
    }),
  );

  const { loading } = useFetch(async () => {
    if (!userUuid) return;
    try {
      const res = await apiGetBillingUsersUserUuidAccount({
        path: { user_uuid: userUuid },
      });
      state.original = res.data.balance;
      state.balance = res.data.balance;
    } catch (error) {
      maybeReportError(error);
      state.error = error;
    }
  }, [userUuid]);

  async function save() {
    if (
      !userUuid ||
      state.balance === undefined ||
      state.balance === state.original
    ) {
      return;
    }
    await apiPutBillingUsersUserUuidCredits({
      path: { user_uuid: userUuid },
      body: {
        balance: state.balance,
        note: state.note.trim(),
      },
    });
    state.original = state.balance;
  }

  return { state, loading, save };
}

type ScreeningCredits = ReturnType<typeof useScreeningCredits>;

interface CreditsState {
  /** The balance as loaded; undefined until then, or when loading failed. */
  original: number | undefined;
  balance: number | undefined;
  note: string;
  error: unknown;
}

/**
 * The row at the bottom of the user edit dialog's `FormTable`: the balance
 * with a pencil, like the other pickers there. The modal only stages the new
 * figure and its reason; the dialog's Save writes them.
 */
export const ScreeningCreditsRows = observer(function ScreeningCreditsRows({
  credits: { state, loading },
  username,
}: {
  credits: ScreeningCredits;
  username: string;
}) {
  const { t } = useLingui();

  if (loading) {
    return <FormRow label={t`Credits left`} data={<Spinner size={20} />} />;
  }

  if (state.original === undefined) {
    return (
      <FormRow
        label={t`Credits left`}
        data={
          <span className="text-danger-foreground text-sm">
            {state.error instanceof RequestError &&
            state.error.response.status === 404 ? (
              <Trans>This user has no billing account.</Trans>
            ) : (
              errorText(state.error)
            )}
          </span>
        }
      />
    );
  }

  const changed = state.balance !== state.original;

  return (
    <FormRow
      label={t`Credits left`}
      data={
        <div className="flex flex-wrap items-center gap-2">
          <ChooseField
            label={
              changed
                ? `${state.original} → ${state.balance}`
                : String(state.original)
            }
            modal={({ onClose }) => (
              <CreditsModal
                state={state}
                username={username}
                onClose={onClose}
              />
            )}
          />
          {changed && (
            <span className="text-brand-primary text-xs font-semibold">
              <Trans>Unsaved - click Save to apply</Trans>
            </span>
          )}
        </div>
      }
    />
  );
});

const CreditsModal = observer(function CreditsModal({
  state,
  username,
  onClose,
}: {
  state: CreditsState;
  username: string;
  onClose: () => void;
}) {
  // A copy, so Cancel leaves the staged change as it was.
  const [draft] = useState(() =>
    observable({ balance: state.balance, note: state.note }),
  );
  const changed = draft.balance !== state.original;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <RecordDialogTitle>
        <Trans>Change credits: {username}</Trans>
      </RecordDialogTitle>
      <Form
        onSubmit={() => {
          state.balance = draft.balance;
          state.note = changed ? draft.note : "";
          onClose();
        }}
      >
        <FormTable dataAdd>
          <FormRow
            label={<Trans>Current credits</Trans>}
            data={state.original}
          />
          <FormRow
            label={<Trans>New amount</Trans>}
            data={
              <NumberInput
                attr="balance"
                model={draft}
                required
                autofocus
                min={BALANCE.minimum}
                max={BALANCE.maximum}
                step={1}
                width={120}
              />
            }
            mandatory
          />
          <FormRow
            label={<Trans>Reason for change</Trans>}
            hint={<Trans>Saved with the change in the credits history.</Trans>}
            data={
              <TextInput
                attr="note"
                model={draft}
                required={changed}
                minLength={changed ? NOTE.minLength : undefined}
                maxLength={NOTE.maxLength}
              />
            }
            mandatory={changed}
          />
        </FormTable>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit">
            <Trans>Confirm</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </Dialog>
  );
});
